import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  registerUser,
  loginUser,
  setupAxiosInterceptors,
  resendVerificationEmail
} from '../services/authService';
import { universalAuthService } from '../services/universalAuthService';
import UserService from '../services/userService';
import { User } from '../types/user';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

// Интерфейс аутентификации пользователя (расширение базового User)
export interface AuthUser extends User {
  isAdmin: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  initializing: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, phone: string) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isEmailVerified: () => boolean;
  resendVerification: () => Promise<boolean>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Функция для декодирования JWT токена
const decodeJwtToken = (token: string): any => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    return JSON.parse(atob(parts[1]));
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState<boolean>(true);
  const navigate = useNavigate();

  // Инициализируем автоматическое обновление токенов
  useTokenRefresh();

  // Инициализация при старте приложения (только один раз)
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        console.log('Initializing user auth...');
        // Проверяем был ли пользователь залогинен до инициализации
        const hadStoredData = !!localStorage.getItem('user');
        const userData = await universalAuthService.initializeAuth('user');

        if (!isMounted) return;

        if (userData) {
          // Проверяем роль - если это поле, не инициализируем в AuthContext
          if (userData.role === 'field') {
            console.log('Field detected in user auth, clearing user data');
            universalAuthService.clearData('user');
            return;
          }

          const { token } = universalAuthService.getStoredData('user');
          const normalizedUser = { ...userData } as AuthUser;

          let normalizedRole =
            typeof normalizedUser.role === 'string' && normalizedUser.role.length > 0
              ? normalizedUser.role
              : undefined;

          if (!normalizedRole && token) {
            const tokenPayload = decodeJwtToken(token);
            const claimedRole = tokenPayload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
            if (claimedRole) {
              normalizedRole = claimedRole;
            }
          }

          normalizedRole = normalizedRole ?? 'customer';
          normalizedUser.role = normalizedRole;
          normalizedUser.isAdmin = normalizedUser.isAdmin === true || normalizedRole.toLowerCase() === 'admin';
          normalizedUser.emailVerified = normalizedUser.emailVerified ?? true;

          setUser(normalizedUser);
          console.log('User set successfully');

          // Настраиваем axios interceptors для пользователя и синхронизируем сторедж
          if (token) {
            universalAuthService.saveData('user', normalizedUser, token);
            setupAxiosInterceptors(token);
          }
        } else {
          console.log('No valid user data found');
          // Если были данные но токен истёк - редирект на главную
          if (hadStoredData) {
            console.log('Token expired, redirecting to homepage');
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Error initializing user auth:', error);
        universalAuthService.clearData('user');
      } finally {
        if (isMounted) {
          setInitializing(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Только при монтировании

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await loginUser(email, password);
      
      // Извлекаем роль из JWT токена
      const tokenPayload = decodeJwtToken(response.token);
      const userRole = tokenPayload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 'customer';
      
      // Если это поле или судья, перенаправляем данные в соответствующие контексты
      if (userRole === 'field') {
        // Сохраняем данные поля в правильном ключе
        universalAuthService.saveData('field', response.user, response.token, response.refreshToken ?? undefined);
        // Очищаем данные из user ключа
        universalAuthService.clearData('user');
        // Перенаправляем на страницу поля
        window.location.href = `/fields/${response.user.id}`;
        return;
      }
      
      if (userRole === 'referee') {
        console.log('=== REFEREE LOGIN DEBUG ===');
        console.log('Login response.user:', response.user);
        console.log('isMainReferee:', (response.user as any).isMainReferee);
        console.log('fieldAssignments:', (response.user as any).fieldAssignments);
        console.log('Detected role:', userRole);
        console.log('===========================');

        // Сохраняем данные судьи в правильном ключе
        universalAuthService.saveData('referee', response.user, response.token, response.refreshToken ?? undefined);
        // Очищаем данные из user ключа
        universalAuthService.clearData('user');
        // Перенаправляем на страницу судьи
        window.location.href = '/referee/profile';
        return;
      }
      
      // Сохраняем данные авторизации через универсальный сервис
      universalAuthService.saveData('user', response.user, response.token, response.refreshToken ?? undefined);
      setupAxiosInterceptors(response.token);
      
      // Устанавливаем пользователя
      const authUser: AuthUser = {
        id: response.user.id,
        name: response.user.name,
        email: response.user.email,
        phone: (response.user as any).phone || '',
        role: userRole,
        status: (response.user as any).status || 'Active',
        isActive: true,
        emailVerified: true, // Если пользователь залогинился, значит email верифицирован
        isAdmin: userRole === 'admin',
        
        // Кланы
        airsoftClanId: response.user.airsoftClanId || '',
        airsoftClanName: response.user.airsoftClanName || '',
        airsoftClanRole: response.user.airsoftClanRole || '',
        paintballClanId: response.user.paintballClanId || '',
        paintballClanName: response.user.paintballClanName || '',
        paintballClanRole: response.user.paintballClanRole || '',
        
        // Основной клан (используем первый доступный)
        clanId: response.user.airsoftClanId || response.user.paintballClanId || '',
        
        // Базы и локация
        state: (response.user as any).state || '',
        primaryBase: (response.user as any).primaryBase || '',
        secondaryBases: (response.user as any).secondaryBases || [],
        gameDisplayPreference: (response.user as any).gameDisplayPreference || 'all',
        
        // RFID
        rfid: (response.user as any).rfid || [],
        
        // Ранги (новая структура)
        ranks: (response.user as any).ranks || {
          airsoftHumanRank: { rankName: 'Recruit', points: 0 },
          airsoftAlienRank: { rankName: 'Novice', points: 0 },
          paintballHumanRank: { rankName: 'Recruit', points: 0 },
          paintballAlienRank: { rankName: 'Novice', points: 0 },
          speedballHumanRank: { rankName: 'Recruit', points: 0 },
          speedballAlienRank: { rankName: 'Novice', points: 0 }
        },
        
        // Статистика игр (новая структура)
        gameStatistics: (response.user as any).gameStatistics || {
          totalGames: 0,
          winRate: 0,
          totalWins: 0,
          totalAirsoftGames: 0,
          totalPaintballGames: 0,
          totalSpeedballGames: 0
        },
        
        // Достижения
        achievementsIds: (response.user as any).achievementsIds || [],
        
        // Временные метки
        createdAt: response.user.createdAt || new Date().toISOString(),
        updatedAt: response.user.updatedAt || new Date().toISOString()
      };
      setUser(authUser);
      universalAuthService.saveData('user', authUser, response.token);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Authentication error';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (name: string, email: string, password: string, phone: string) => {
    setLoading(true);
    setError(null);
    
    try {
      await registerUser(name, email, password, phone);
      
      // Больше не сохраняем данные и не устанавливаем пользователя после регистрации,
      // так как пользователь должен сначала подтвердить email
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Registration error';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    universalAuthService.clearData('user');
    navigate('/');
  }, [navigate]);
  
  // Метод для проверки прав администратора
  const isAdmin = useCallback(() => {
    return user?.isAdmin === true || user?.role === 'Admin';
  }, [user]);
  
  // Метод для проверки верификации email
  const isEmailVerified = useCallback(() => {
    return user?.emailVerified === true;
  }, [user]);
  
  // Метод для повторной отправки верификационного email
  const resendVerification = useCallback(async (): Promise<boolean> => {
    if (!user?.email) {
      return false;
    }
    
    return await resendVerificationEmail(user.email);
  }, [user]);

  // Метод для обновления данных пользователя
  const refreshUserData = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    try {
      const updatedUserData = await UserService.getPlayerProfile(user.id);
      
      // Если пользователь залогинен, значит email верифицирован
      const currentRole = user.role;
      
      // Преобразуем данные в формат AuthUser
      const updatedUser: AuthUser = {
        id: updatedUserData.id,
        name: updatedUserData.name,
        email: updatedUserData.email,
        phone: (updatedUserData as any).phone || user.phone || '',
        role: currentRole, // Сохраняем текущую роль из токена
        status: (updatedUserData as any).status || user.status,
        isActive: (updatedUserData as any).isActive !== undefined ? (updatedUserData as any).isActive : user.isActive,
        emailVerified: true, // Если пользователь залогинен, значит email верифицирован
        isAdmin: (updatedUserData as any).isAdmin || user.isAdmin,
        
        // Кланы
        airsoftClanId: (updatedUserData as any).airsoftClanId || user.airsoftClanId || '',
        airsoftClanName: (updatedUserData as any).airsoftClanName || user.airsoftClanName || '',
        airsoftClanRole: (updatedUserData as any).airsoftClanRole || user.airsoftClanRole || '',
        paintballClanId: (updatedUserData as any).paintballClanId || user.paintballClanId || '',
        paintballClanName: (updatedUserData as any).paintballClanName || user.paintballClanName || '',
        paintballClanRole: (updatedUserData as any).paintballClanRole || user.paintballClanRole || '',
        
        // Основной клан (используем первый доступный)
        clanId: (updatedUserData as any).airsoftClanId || (updatedUserData as any).paintballClanId || user.airsoftClanId || user.paintballClanId || '',
        
        // Базы и локация
        state: (updatedUserData as any).state || user.state || '',
        primaryBase: (updatedUserData as any).primaryBase || user.primaryBase || '',
        secondaryBases: (updatedUserData as any).secondaryBases || user.secondaryBases || [],
        gameDisplayPreference: (updatedUserData as any).gameDisplayPreference || user.gameDisplayPreference,
        
        // RFID
        rfid: (updatedUserData as any).rfid || user.rfid || [],
        
        // Ранги (новая структура)
        ranks: (updatedUserData as any).ranks || user.ranks || {
          airsoftHumanRank: { rankName: 'Recruit', points: 0 },
          airsoftAlienRank: { rankName: 'Novice', points: 0 },
          paintballHumanRank: { rankName: 'Recruit', points: 0 },
          paintballAlienRank: { rankName: 'Novice', points: 0 },
          speedballHumanRank: { rankName: 'Recruit', points: 0 },
          speedballAlienRank: { rankName: 'Novice', points: 0 }
        },
        
        // Статистика игр (новая структура)
        gameStatistics: (updatedUserData as any).gameStatistics || user.gameStatistics || {
          totalGames: 0,
          winRate: 0,
          totalWins: 0,
          totalAirsoftGames: 0,
          totalPaintballGames: 0,
          totalSpeedballGames: 0
        },
        
        // Достижения
        achievementsIds: (updatedUserData as any).achievementsIds || user.achievementsIds || [],
        
        createdAt: (updatedUserData as any).createdAt || user.createdAt,
        updatedAt: (updatedUserData as any).updatedAt || user.updatedAt
      };

      // Обновляем пользователя в контексте
      setUser(updatedUser);
      
      // Обновляем данные в localStorage
      const { token, refreshToken } = universalAuthService.getStoredData('user');
      if (token) {
        universalAuthService.saveData('user', updatedUser, token, refreshToken ?? undefined);
      }
    } catch (error) {
      // Silent failure for refresh
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error,
      initializing,
      login, 
      register, 
      logout, 
      isAdmin,
      isEmailVerified,
      resendVerification,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 
